import React from "react";

/**
 * Assigns a value to a React ref, handling both callback refs and object refs.
 *
 * @param ref - A React ref (callback, object, or null/undefined).
 * @param el  - The element (or null) to assign.
 */
export function setRef<T>(ref: React.Ref<T>, el: T | null) {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(el);
  } else {
    (ref as React.RefObject<T | null>).current = el;
  }
}

/**
 * Combines multiple React refs into a single callback ref.
 *
 * Accepts any mix of callback refs, object refs (`useRef`), and nulls.
 * Returns a stable callback ref that forwards the element to all provided refs.
 *
 * @example
 * // Combine a local ref with a forwarded ref and a library ref:
 * const localRef = useRef<HTMLDivElement>(null);
 * <div ref={mergeRefs(localRef, forwardedRef, dragRef)} />
 *
 * @param refs - Any number of React refs to merge.
 * @returns A single `RefCallback<T>` that sets all provided refs.
 */
export function mergeRefs<T>(...refs: React.Ref<T>[]): React.RefCallback<T> {
  return (el: T | null) => {
    refs.forEach(r => setRef(r, el));
  };
}
